import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Subject, Observable, Subscription, take, filter } from 'rxjs';
import { InactivityModalComponent } from '../components/inactivity-modal/inactivity-modal.component';

@Injectable({
  providedIn: 'root'
})
export class InactivityService implements OnDestroy {
  /** Inactivity timeout in milliseconds (90 minutes) */
  private readonly INACTIVITY_TIME = 90 * 60 * 1000;

  /** Timer ID for tracking inactivity */
  private timeoutId!: NodeJS.Timeout;

  /** Subject to trigger inactivity resets */
  private inactivitySubject = new Subject<boolean>();

  /** Routes where the inactivity modal should NOT be shown */
  private excludedRoutes: string[] = ['/manage', '/simulations', '/predictor'];

  /** Subscription for router events */
  private routerSubscription!: Subscription;

  constructor(
    private ngZone: NgZone,
    private router: Router,
    private dialogService: DialogService
  ) {
    // Subscribe to inactivity resets
    this.inactivitySubject.subscribe(() => this.resetTimer());

    // Reset inactivity timer on navigation and store subscription reference
    this.routerSubscription = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => this.resetInactivityTimer());
  }

  /**
   * Resets the inactivity timer when user activity is detected.
   */
  public resetInactivityTimer(): void {
    this.inactivitySubject.next(true);
  }

  /**
   * Returns an observable that emits when user activity is detected.
   */
  public getUserActivity(): Observable<boolean> {
    return this.inactivitySubject.asObservable();
  }

  /**
   * Resets the inactivity timer and schedules a new timeout.
   */
  private resetTimer(): void {
    // Clear existing timeout if it exists
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }

    // Start a new timeout outside of Angular's zone to optimize performance
    this.ngZone.runOutsideAngular(() => {
      this.timeoutId = setTimeout(() => {
        this.ngZone.run(() => this.handleInactivity());
      }, this.INACTIVITY_TIME);
    });
  }

  /**
   * Handles inactivity by checking the current route before showing the modal.
   */
  private handleInactivity(): void {
    if (this.isExcludedRoute()) {
      return;
    }

    this.openInactivityModal();
  }

  /**
   * Opens the inactivity warning modal.
   */
  private openInactivityModal(): void {
    const inactivityModal: DynamicDialogRef = this.dialogService.open(
      InactivityModalComponent,
      {
        width: '600px',
        header: '',
        dismissableMask: true,
        styleClass: 'bottom-rounded title-free-header',
        modal: true,
      }
    );

    // Reload the page when the modal is closed
    inactivityModal.onClose.pipe(take(1)).subscribe(() => {
      window.location.reload();
    });
  }

  /**
   * Checks if the current route is in the excluded routes list.
   * @returns {boolean} True if the route is excluded, false otherwise.
   */
  private isExcludedRoute(): boolean {
    return this.excludedRoutes.includes(this.router.url);
  }

  /**
   * Cleanup subscriptions
   */
  ngOnDestroy(): void {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }
}
